import type { OperationLogEntry, Protocol } from "../types";

const MAX_LOG_ENTRIES = 1000;

export interface SessionLogContext {
  sessionId?: string;
  deviceLabel?: string;
  jobId?: string;
  connectionEpoch?: number;
}

export class OperationLogger {
  private entries: OperationLogEntry[] = [];
  private listeners = new Set<(entries: OperationLogEntry[]) => void>();

  subscribe(listener: (entries: OperationLogEntry[]) => void): () => void {
    this.listeners.add(listener);
    listener(this.entries);
    return () => this.listeners.delete(listener);
  }

  add(entry: Omit<OperationLogEntry, "timestamp">): void {
    this.entries = [...this.entries, { ...entry, timestamp: new Date().toISOString() }].slice(-MAX_LOG_ENTRIES);
    this.listeners.forEach((listener) => listener(this.entries));
  }

  info(protocol: Protocol, operation: string, message: string, ctx?: SessionLogContext): void {
    this.add({ protocol, operation, status: "info", message, ...ctx });
  }

  start(protocol: Protocol, operation: string, message = "Bắt đầu thao tác", ctx?: SessionLogContext): number {
    const startedAt = performance.now();
    this.add({ protocol, operation, status: "started", message, ...ctx });
    return startedAt;
  }

  success(protocol: Protocol, operation: string, startedAt: number, message: string, bytes?: number, ctx?: SessionLogContext): void {
    this.add({ protocol, operation, status: "ok", durationMs: Math.round(performance.now() - startedAt), bytes, message, ...ctx });
  }

  failure(protocol: Protocol, operation: string, startedAt: number, message: string, errorCode?: OperationLogEntry["errorCode"], ctx?: SessionLogContext): void {
    this.add({ protocol, operation, status: "failed", durationMs: Math.round(performance.now() - startedAt), errorCode, message, ...ctx });
  }

  get snapshot(): OperationLogEntry[] {
    return [...this.entries];
  }

  clear(): void {
    this.entries = [];
    this.listeners.forEach((listener) => listener(this.entries));
  }

  exportText(): string {
    return this.entries
      .map((entry) => `${entry.timestamp} [${entry.protocol}]${entry.deviceLabel ? ` [${entry.deviceLabel}]` : ""}${entry.jobId ? ` [job:${entry.jobId}]` : ""}${entry.connectionEpoch !== undefined ? ` [epoch:${entry.connectionEpoch}]` : ""} ${entry.operation} ${entry.status.toUpperCase()}${entry.errorCode ? ` ${entry.errorCode}` : ""} - ${entry.message}`)
      .join("\n");
  }

  exportJson(): string {
    return JSON.stringify(this.entries, null, 2);
  }
}

export const operationLogger = new OperationLogger();
